import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { FieldType, PostFormWithFile } from "@shoothill/core";
import { AppUrls } from "AppUrls";
// Models
import { DeclarationModel } from "Globals/Models/Domain/FormSections/Declarations/DeclarationModel";
import { DeclarationDTO } from "Globals/Models/DTO/DeclarationDTO";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { StoresInstance } from "Globals/Stores";
// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";
import { action, computed, observable, runInAction } from "mobx";
import moment from "moment";
import validator from "validator";

export default class DeclarationViewModel extends FormSectionViewModelBase<DeclarationModel> {
    @observable public dataUrl: string = "";
    @observable public signatureURL: string = "";
    constructor(DeclarationModel: DeclarationModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, DeclarationModel);
        this.saveStore.addCallback(FormSectionsEnum.Declaration, this.save);
        this.saveFunction = this.save;
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.Declaration, this.canProceed);
        }
    }

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);
                let sigRes = await this.saveSignature();
                let upsertDeclaration: DeclarationDTO = this.model.toDto();
                const apiResult = await this.Post<DeclarationDTO>(AppUrls.Server.ApplicationForm.Upsert.Declaration, upsertDeclaration);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("Declaration:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.Declaration, this.canProceed);

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("Declaration:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };
    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    @action
    public handleInputChange = (): void => {
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.Declaration, this.canProceed);
    };

    @action
    public handleSignatureChange = async (dataUrl: string): Promise<void> => {
        runInAction(() => {
            if (dataUrl == "") {
                this.signatureURL = "";
            }
            this.dataUrl = dataUrl;
        });
    };

    @action
    public loadSignature = async (): Promise<void> => {
        if (this.model.signature == "") return;
        const downloadURL = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, this.model.signature, undefined, { responseType: "blob" });
        const response = downloadURL as any;
        const url = window.URL.createObjectURL(new Blob([response]));
        runInAction(() => {
            this.signatureURL = url;
        });
    };
    @action
    public saveSignature = async (): Promise<void> => {
        if (this.dataUrl == "") {
            this.model.setDirty("signature", true);
            this.model.setValue("signature", "");
            runInAction(() => {
                this.signatureURL = "";
            });
        } else {
            let imageBlob = await fetch(this.dataUrl).then((r) => r.blob());
            const apiResult = await PostFormWithFile<string>(AppUrls.Server.File.UploadSignature, new File([imageBlob], "signature.png"));
            if (apiResult.wasSuccessful) {
                this.model.setDirty("signature", true);
                this.model.setValue("signature", apiResult.payload);
                const downloadURL = await this.Post<Blob>(AppUrls.Server.File.DownloadFile, apiResult.payload, undefined, { responseType: "blob" });
                const response = downloadURL as any;
                const url = window.URL.createObjectURL(new Blob([response]));
                runInAction(() => {
                    this.signatureURL = url;
                });
            }
        }
        this.handleInputChange();
    };

    @action
    public handleCanContactChange = (val: boolean): void => {
        this.model.setValue("canContact", val);
        this.model.setDirty("canContact", true);
        if (!val) {
            (["contactByEmail", "contactByPhone", "contactBySms", "contactByPost"] as const).forEach((field) => {
                this.model.setValue(field, false);
                this.model.setDirty(field, true);
            });
        }
        this.handleInputChange();
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<DeclarationModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleFieldChange = (fieldName: keyof FieldType<DeclarationModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };
    @action
    public handleRadioChange = (fieldName: keyof FieldType<DeclarationModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<DeclarationModel>, val: MaterialUiPickersDate) {
        this.model.setDirty(fieldName, true);
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    @computed
    public get canProceed(): boolean {
        let completed = true;
        Object.keys(this.model).forEach((_field) => {
            let field = _field as keyof FieldType<DeclarationModel>;
            let value = this.model.getValue(field);
            if (field == "createdDate") return;
            if (field == "isDeleted") return;
            if (value == "placeholder") {
                completed = false;
            }
            if (this.isEmptyNullOrUndefined(value) == true) {
                completed = false;
            }
            if (completed == false) {
                // console.error("Field not completed: ", field, "Value: ", value);
            }
        });
        return completed;
    }

    public getEditableCopyByRouteAndPlaceholderNumber = (route: string, placeholderNumber: number) => {
        let editableCopyRoute = StoresInstance.Domain.editableCopyRoutes.find((r) => r.route === route);

        if (editableCopyRoute !== undefined) {
            let editableCopyRouteId: string = editableCopyRoute.id;
            return StoresInstance.Domain.editableCopies.find((c) => c.routeId === editableCopyRouteId && c.placeholderNumber === placeholderNumber)?.copyValue as string;
        }

        return "";
    };

    public async isFieldValid(fieldName: keyof FieldType<DeclarationModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    public async isValid(fieldName: keyof FieldType<DeclarationModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "signature") {
            if ((value && !validator.isEmpty(value)) || isRequired) {
                if (validator.isEmpty(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (fieldName == "signedDate") {
            let date = value as Date;
            if ((date && !validator.isEmpty(date.toString())) || isRequired) {
                if (date == null || validator.isEmpty(date.toString())) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }
        if (isValid == false) {
            console.log("Field Not valid: ", fieldName);
        }
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<DeclarationModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }
    public beforeUpdate: undefined;
}
